import {
  getCondition,
  getConditionColor,
  isUpperTooth,
  type ToothFace,
} from '../constants/odontology';
import type { DentalChartEntry, DentalEntryStatus } from '../types/odontology';

/**
 * Traduz os lançamentos do odontograma no estado visual de cada dente.
 *
 * O odontograma é desenhado em vista oclusal: a face vestibular aparece na
 * borda externa da arcada e a lingual/palatina na borda interna. Mesial é
 * sempre o lado voltado para a linha média, o que inverte esquerda e direita
 * entre as hemiarcadas.
 */

/**
 * Preenchimento de uma região do dente. O status acompanha a cor porque o
 * odontograma distingue "a realizar" de "realizado" por textura (hachura vs
 * sólido), não só por matiz — o que mantém a leitura correta em impressão
 * preto e branco e para quem tem discromatopsia.
 */
export interface ToothFill {
  color: string;
  status: DentalEntryStatus;
}

export interface ToothView {
  /** Preenchimento por face, derivado da condição mais recente. */
  faceFills: Partial<Record<ToothFace, ToothFill>>;
  /** Condição que se aplica ao dente inteiro (coroa, implante, canal…). */
  toothFill: ToothFill | null;
  /** Dente ausente, extraído ou agenésico — renderizado vazado. */
  absent: boolean;
  /** Quantidade de lançamentos por status, usada nos contadores. */
  counts: Record<DentalEntryStatus, number>;
  entries: DentalChartEntry[];
}

export type ToothViewMap = Record<string, ToothView>;

const EMPTY_COUNTS: Record<DentalEntryStatus, number> = {
  pre_existing: 0,
  planned: 0,
  in_progress: 0,
  completed: 0,
  cancelled: 0,
};

function createToothView(): ToothView {
  return { faceFills: {}, toothFill: null, absent: false, counts: { ...EMPTY_COUNTS }, entries: [] };
}

function entryTimestamp(entry: DentalChartEntry): number {
  return new Date(entry.updated_at || entry.created_at || 0).getTime();
}

/**
 * Lançamentos cancelados nunca pintam o odontograma; os demais são aplicados do
 * mais antigo para o mais recente, de modo que o último achado prevaleça.
 */
export function buildToothViewMap(
  entries: DentalChartEntry[],
  statusFilter?: DentalEntryStatus | 'all',
): ToothViewMap {
  const map: ToothViewMap = {};

  const relevant = entries
    .filter((entry) => entry.tooth_number && entry.status !== 'cancelled')
    .filter((entry) => !statusFilter || statusFilter === 'all' || entry.status === statusFilter)
    .sort((a, b) => entryTimestamp(a) - entryTimestamp(b));

  relevant.forEach((entry) => {
    const tooth = entry.tooth_number as string;
    if (!map[tooth]) map[tooth] = createToothView();
    const view = map[tooth];

    view.entries.push(entry);
    view.counts[entry.status] = (view.counts[entry.status] || 0) + 1;

    const condition = getCondition(entry.condition_code);
    const color = entry.color || (entry.condition_code ? getConditionColor(entry.condition_code) : null);
    const fill: ToothFill | null = color ? { color, status: entry.status } : null;

    if (condition?.absent) {
      view.absent = true;
      view.toothFill = fill;
      return;
    }

    // Um lançamento posterior que não seja de ausência reabilita o dente
    // (ex.: implante instalado na posição de um dente extraído).
    view.absent = false;

    if (entry.faces?.length) {
      entry.faces.forEach((face) => {
        if (fill) view.faceFills[face as ToothFace] = fill;
      });
    } else if (fill) {
      view.toothFill = fill;
    }
  });

  return map;
}

export function getToothView(map: ToothViewMap, toothNumber: string): ToothView {
  return map[toothNumber] || createToothView();
}

/**
 * Posição de cada face na grade 3x3 do desenho. Depende da arcada (vestibular
 * para fora) e da hemiarcada (mesial para a linha média).
 */
export interface ToothFaceLayout {
  top: ToothFace;
  bottom: ToothFace;
  left: ToothFace;
  right: ToothFace;
  center: ToothFace;
}

const RIGHT_SIDE_QUADRANTS = new Set(['1', '4', '5', '8']);

export function getToothFaceLayout(toothNumber: string): ToothFaceLayout {
  const upper = isUpperTooth(toothNumber);
  const patientRightSide = RIGHT_SIDE_QUADRANTS.has(toothNumber.charAt(0));

  return {
    top: upper ? 'V' : 'L',
    bottom: upper ? 'L' : 'V',
    left: patientRightSide ? 'D' : 'M',
    right: patientRightSide ? 'M' : 'D',
    center: 'O',
  };
}

/**
 * Achados que a vista lateral consegue representar. São os que envolvem raiz,
 * canal ou perfil da coroa — o que a vista oclusal, por definição, não mostra.
 */
export interface ToothLateralView {
  /** Endodontia: preenche a câmara e os canais. */
  endo: ToothFill | null;
  /** Lesão periapical: halo radiolúcido no ápice. */
  periapical: ToothFill | null;
  /** Implante: a fixação substitui a raiz. */
  implant: ToothFill | null;
  /** Coroa protética, pôntico ou prótese fixa: preenche o perfil da coroa. */
  crownProsthesis: ToothFill | null;
  /** Núcleo/pino intrarradicular. */
  post: ToothFill | null;
  fracture: ToothFill | null;
  /** Bráquete ou contenção sobre a face vestibular. */
  orthodontic: ToothFill | null;
  /** Resto radicular: a coroa não existe mais. */
  rootRemnant: boolean;
  extractionPlanned: boolean;
}

const LATERAL_SLOT_BY_CONDITION: Record<string, keyof ToothLateralView> = {
  canal_tratado: 'endo',
  canal_indicado: 'endo',
  retratamento: 'endo',
  lesao_periapical: 'periapical',
  implante: 'implant',
  coroa: 'crownProsthesis',
  coroa_provisoria: 'crownProsthesis',
  protese_fixa: 'crownProsthesis',
  pontico: 'crownProsthesis',
  nucleo: 'post',
  fratura: 'fracture',
  aparelho_fixo: 'orthodontic',
  contencao: 'orthodontic',
};

const EMPTY_LATERAL: ToothLateralView = {
  endo: null,
  periapical: null,
  implant: null,
  crownProsthesis: null,
  post: null,
  fracture: null,
  orthodontic: null,
  rootRemnant: false,
  extractionPlanned: false,
};

export function buildToothLateralView(view: ToothView): ToothLateralView {
  const lateral: ToothLateralView = { ...EMPTY_LATERAL };

  // `view.entries` já vem do mais antigo para o mais recente, então o último
  // achado de cada tipo prevalece — mesma regra da vista oclusal.
  view.entries.forEach((entry) => {
    const code = entry.condition_code;
    if (!code) return;

    if (code === 'resto_radicular') {
      lateral.rootRemnant = true;
      return;
    }
    if (code === 'extracao_indicada') {
      lateral.extractionPlanned = true;
      return;
    }

    const slot = LATERAL_SLOT_BY_CONDITION[code];
    if (!slot) return;

    const color = entry.color || getConditionColor(code);
    (lateral[slot] as ToothFill) = { color, status: entry.status };
  });

  return lateral;
}

export function summarizeEntries(entries: DentalChartEntry[]): Record<DentalEntryStatus, number> {
  const counts = { ...EMPTY_COUNTS };
  entries.forEach((entry) => {
    counts[entry.status] = (counts[entry.status] || 0) + 1;
  });
  return counts;
}
